import { useContext, useEffect } from "react";
import useSessionUser from "utils/hooks/useSessionUser";
import IconUser from "../assets/img/user-avatar.svg";
import cx from "classnames";
import { IFormUserData, IUser } from "utils/types";
import { FormProvider, useForm } from "react-hook-form";
import Input from "components/Form/Input/Input";
import AddressSelect from "components/Form/AddressSelect/AddressSelect";
import { AppContext } from "contexts/AppContext";
import { redirectToService } from "utils/utils";
import apiClient from "../../../utils/apiClient";
import Select from "components/Form/Select/Select";
import { paysMDM } from "utils/paysMDM";
import { genderMDM } from "utils/genderMDM";
import { activityMDM } from "utils/activityMDM";


function User({ firstName, lastName }: { firstName: string, lastName: string }) {
  return (
    <div className="user">
      <img src={IconUser} alt="" />
      <span className="user-text">
        <span>Bienvenue</span>
        <span>{firstName} {lastName}</span>
      </span>
    </div>
  )
}

function FormHeader() {
  return (
    <div className="form-header">
      <div className="title">
        Compléter mes données
      </div>
      <div className="subtitle">Informations communes aux services VNF</div>
    </div>
  )
}

function getUserAddress(user: IUser) {
  let defaultAdress = '';
  if (user.mdm) {
    const { street, housenumber, postalCode, city } = user.mdm?.address;
    if (street === "INCONNU") {
      defaultAdress = `INCONNUE`;
    } else {
      defaultAdress = `${housenumber} ${street} ${postalCode} ${city}`;
    }
  }
  return defaultAdress;
}

async function createOrUpdateMDM(data: IFormUserData, token: string) {
  const result = {
    status: "",
    message: ""
  }

  try {
    const update = await apiClient.put(
      `${import.meta.env.REACT_APP_API_URL}/api/mdm/users`,
      data,
      {
        headers: {
          Authorization: `Bearer ${token}`
        }
      }
    );

    if (update.status === 200) {
      result.status = "OK";
    } else {
      result.status = String(update.status) || "KO";
      const msg = update?.data?.error || "Une erreur est survenue";
      const extra = update?.data?.message;
      result.message = extra && extra !== msg ? `${msg} (${extra})` : msg;
    }
  } catch (error) {
    console.error(error);
    result.status = "KO";
    const msg = error?.response?.data?.error || "Une erreur est survenue";
    const extra = error?.response?.data?.message;
    result.message = extra && extra !== msg ? `${msg} (${extra})` : msg;
  }

  console.log('result', result);
  return result;
}

export default function Form() {
  /** HOOKS */
  const {
    user,
    token
  } = useSessionUser();
  const methods = useForm<IFormUserData>({ mode: "all" });
  const { isMobile } = useContext(AppContext);

  /** VARIABLES */
  const { handleSubmit, trigger, formState: { errors, isDirty, isSubmitting }, setValue } = methods;
  const hasUserMDM = !!user?.mdm;


  /** UseEffects */
  useEffect(() => {
    if (!user) {
      return;
    }

    setValue("firstName", user.mdm?.firstName || user.firstName);
    setValue("lastName", user.mdm?.lastName || user.lastName);
    setValue("email", user.email);

    if (!user.mdm) {
      return;
    }
    setValue("gender", genderMDM.find(a => a.code === user.mdm?.gender))
    setValue("activity", activityMDM.find(a => a.code === user.mdm?.activityCode))
    setValue("housenumber", user.mdm.address.housenumber);
    setValue("postalCode", user.mdm.address.postalCode);
    setValue("street", user.mdm.address.street);
    setValue("phoneNumber", user.mdm.phoneNumber);
    setValue("country", paysMDM.find(a => a.id === user.mdm?.country));
    // setValue("language", user.language);

    const { street, housenumber, postalCode, city } = user.mdm.address;
    setValue("city", city);
    if (street === "INCONNU") {
      setValue("address", `inconnue`);
    } else {
      setValue("address", `${housenumber} ${street} ${postalCode} ${city}`);
    }
  }, [setValue, user]);

  async function onSubmit(data: IFormUserData) {
    if (!token) {
      window.location.reload();
    } else {
      const isFormValid = await trigger();

      if (!isFormValid) {
        console.log('Erreurs:', errors);
        return;
      }

      console.log('Submitting data to MDM:', data);
      const result = await createOrUpdateMDM(data, token);
      if (result.status !== "OK") {
        let message = `Une erreur est survenue. Merci de réessayer ultérieurement`
        if (result.message) {
          message = `Une erreur est survenue avec le message : ${result.message}. Merci de réessayer ultérieurement`
        }
        alert(message);
        return;
      }
      if (import.meta.env.MODE !== "development") {
        redirectToService();
      }
    }
  }

  if (!user) {
    return (
      <div className="form">
        Chargement en cours ...
      </div>
    )
  }

  return (
    <div className={cx("form", {
      mobile: isMobile
    })}>
      <User firstName={user.firstName} lastName={user.lastName} />
      <FormHeader />
      <FormProvider {...methods}>
        <form
          onSubmit={handleSubmit(onSubmit)}
          className="main-form"
        >
          <div className="line-separator" />
          <div className="inner-form">
            <Select
              className="gender"
              name="gender"
              label="Civilité*"
              options={genderMDM}
              defaultValue={user.mdm?.gender || ""}
              errorText={errors?.gender?.message}
              required={(hasUserMDM || isDirty || isSubmitting) && "Civilité manquante"}
            />
            <Input
              defaultValue={user.mdm?.lastName || user.lastName}
              name="lastName"
              required={(hasUserMDM || isDirty || isSubmitting) && "Merci de saisir un nom"}
              label="Nom*"
              errorText={errors?.lastName?.message}
            />
            <Input
              defaultValue={user.mdm?.firstName || user.firstName}
              name="firstName"
              required={(hasUserMDM || isDirty || isSubmitting) && "Merci de saisir un prénom"}
              label="Prénom*"
              errorText={errors?.firstName?.message}
            />
            <AddressSelect
              label="Adresse*"
              defaultValue={getUserAddress(user)}
              errorText={errors?.address?.message}
              required={(hasUserMDM || isDirty || isSubmitting) && "Adresse manquante"}
            />
            <Select
              options={paysMDM}
              className="country"
              defaultValue={paysMDM.find(a => a.id === user.mdm?.country)?.code || ""}
              name="country"
              required={(hasUserMDM || isDirty || isSubmitting) && "Merci de saisir un pays"}
              label="Pays*"
              errorText={errors?.country?.message}
            />
            <Input
              defaultValue={user.mdm?.phoneNumber}
              name="phoneNumber"
              required={(hasUserMDM || isDirty || isSubmitting) && "Merci de saisir un numéro de téléphone"}
              label="Téléphone*"
              errorText={errors?.phoneNumber?.message}
            />
            <Input
              defaultValue={user.email || ""}
              name="email"
              label="Email"
              disabled
            />
            <Select
              className="activity"
              label="Activité"
              name="activity"
              defaultValue={user.mdm?.activityCode || activityMDM.find(a => a.code === "PRO")?.code}
              options={activityMDM}
              hidden={true}
              errorText={errors?.activity?.message}
              required={(hasUserMDM || isDirty || isSubmitting) && "Activité manquante"}
            />
            {/* <Input
              defaultValue={user.language}
              name="language"
              required={(hasUserMDM || isDirty) && "Merci de saisir une langue"}
              label="Langue*"
              errorText={errors?.language?.message}
            /> */}
          </div>
          <div className="line-separator" />
          <div className="buttons">
            <button
              type="submit"
              className="save-btn"
              disabled={isSubmitting}
            >
              Valider
            </button>
          </div>
          <div className="required-fields">
            <span>* Champs obligatoires</span>
          </div>
        </form>
      </FormProvider>
    </div>
  );
}
